import {createReducer, ActionCreator} from '../../../store/utils';
import type {
  MultichatFunnel,
  MultichatFunnelStage,
  MultichatFunnelLane,
} from '../../../entities';
import {setIn, List} from 'immutable';
import {arrayMove, moveBetweenArrays} from '../../../utils/array';

export type MultichatFunnelState = {
  funnels?: MultichatFunnel[],
  funnelStages?: MultichatFunnelStage[],
  currentFunnel?: MultichatFunnel,
};

export type DragAndDropConversationsResult = {
  source: {
    laneId: string,
    index: number,
    conversationId: string,
  },
  destination: {
    laneId: string,
    index: number,
    conversationId: string,
  },
};

export type MultichatFunnelAction = {
  funnels?: MultichatFunnel[],
  funnelStages?: MultichatFunnelStage[],
  funnelStage?: MultichatFunnelStage,
  currentFunnel?: MultichatFunnel,
  lane?: MultichatFunnelLane,
  laneId?: string,
  conversations?: Conversation[],
  conversation: Conversation,
  lanes?: MultichatFunnelLane[],
  dragAndDropResult?: DragAndDropConversationsResult,
  conversationId: string,
};

export const initialState: MultichatFunnelState = {};

export class InitMultichatFunnels extends ActionCreator<MultichatFunnelAction> {}
export class CreateMultichatFunnel extends ActionCreator<MultichatFunnelAction> {}
export class SetCurrentMultichatFunnel extends ActionCreator<MultichatFunnelAction> {}

export class InitCurrentMultichatFunnelConversations extends ActionCreator<MultichatFunnelAction> {}
export class AddLaneToCurrentMultichatFunnel extends ActionCreator<MultichatFunnelAction> {}
export class DeleteLaneFromCurrentMultichatFunnel extends ActionCreator<MultichatFunnelAction> {}
export class ReorderFunnelLanes extends ActionCreator<MultichatFunnelAction> {}

export class AddConversationsToLane extends ActionCreator<MultichatFunnelAction> {}
export class DragAndDropConversations extends ActionCreator<MultichatFunnelAction> {}

export class CreateMultichatFunnelStage extends ActionCreator<MultichatFunnelAction> {}
export class EditMultichatFunnelStage extends ActionCreator<MultichatFunnelAction> {}
export class SetFunnelMainLane extends ActionCreator<MultichatFunnelAction> {}

export class UpsertFunnelConversation extends ActionCreator<MultichatFunnelAction> {}

export class UpdateFunnelConversation extends ActionCreator<MultichatFunnelAction> {}

export class CloseFunnelConversation extends ActionCreator<MultichatFunnelAction> {}
export class RemoveConversationFromLane extends ActionCreator<MultichatFunnelAction> {}

export const actions = {
  initMultichatFunnels: (
    funnels: MultichatFunnel[],
    funnelStages: MultichatFunnelStage[]
  ) =>
    new InitMultichatFunnels({
      funnels,
      funnelStages,
    }),

  setCurrentMultichatFunnel: (funnel: MultichatFunnel) =>
    new SetCurrentMultichatFunnel({
      currentFunnel: funnel,
    }),

  addLaneToCurrentMultichatFunnel: (lane: MultichatFunnelLane) =>
    new AddLaneToCurrentMultichatFunnel({
      lane,
    }),

  initCurrentMultichatFunnelConversations: (
    lanes: MultichatFunnelLane[]
  ): MultichatFunnelAction =>
    new InitCurrentMultichatFunnelConversations({lanes}),

  addConversationsToLane: (
    laneId: string,
    conversations: Conversation[]
  ): MultichatFunnelAction =>
    new AddConversationsToLane({laneId, conversations}),

  createMultichatFunnel: (funnel: MultichatFunnel) =>
    new CreateMultichatFunnel({
      funnel,
    }),
  createMultichatFunnelStage: (funnelStage: MultichatFunnelStage) =>
    new CreateMultichatFunnelStage({
      funnelStage,
    }),
  editMultichatFunnelStage: (funnelStage: MultichatFunnelStage) =>
    new EditMultichatFunnelStage({
      funnelStage,
    }),

  dragAndDropConversations: (
    dragAndDropResult: DragAndDropConversationsResult
  ) =>
    new DragAndDropConversations({
      dragAndDropResult,
    }),

  setFunnelMainLane: (laneId: string) =>
    new SetFunnelMainLane({
      laneId,
    }),

  upsertFunnelConversation: (conversation: Conversation) =>
    new UpsertFunnelConversation({
      conversation,
    }),

  updateFunnelConversation: (conversation: Conversation) =>
    new UpdateFunnelConversation({
      conversation,
    }),

  closeFunnelConversation: (conversationId: string) =>
    new CloseFunnelConversation({
      conversationId,
    }),

  deleteLaneFromCurrentMultichatFunnel: (laneId: string) =>
    new DeleteLaneFromCurrentMultichatFunnel({
      laneId,
    }),

  removeConversationFromLane: (laneId: string, conversationId: string) =>
    new RemoveConversationFromLane({
      laneId,
      conversationId,
    }),

  reorderFunnelLanes: (lanes: MultichatFunnelLane[]) =>
    new ReorderFunnelLanes({
      lanes,
    }),
};

export default createReducer(initialState, {
  [InitMultichatFunnels.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => ({
    ...state,
    funnels: action.funnels,
    funnelStages: action.funnelStages,
  }),
  [CreateMultichatFunnel.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => ({
    ...state,
    funnels: state.funnels
      ? [...state.funnels, action.funnel]
      : [action.funnel],
    currentFunnel: action.funnel,
  }),
  [SetCurrentMultichatFunnel.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    let funnels = state.funnels;
    if (action.currentFunnel && state.funnels) {
      let changed = false;
      const newFunnels = state.funnels.map(f => {
        if (
          f.id === action.currentFunnel?.id &&
          f.name !== action.currentFunnel?.name
        ) {
          changed = true;
          return {...f, name: action.currentFunnel?.name};
        }
        return f;
      });
      if (changed) {
        funnels = newFunnels;
      }
    }
    return {
      ...state,
      currentFunnel: action.currentFunnel,
      funnels,
    };
  },
  [InitCurrentMultichatFunnelConversations.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => ({
    ...state,
    currentFunnel: {
      ...state.currentFunnel,
      lanes: action.lanes,
    },
  }),
  [AddLaneToCurrentMultichatFunnel.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {lane} = action;
    if (!lane) return state;
    const {currentFunnel} = state;
    if (!currentFunnel) return state;

    const newLanes = currentFunnel.lanes
      ? [...currentFunnel.lanes, lane]
      : [lane];

    return {
      ...state,
      currentFunnel: {
        ...currentFunnel,
        lanes: newLanes,
      },
    };
  },
  [CreateMultichatFunnelStage.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const newStage = action.funnelStage;
    if (!newStage) return state;
    const newFunnelStages = state.funnelStages
      ? [...state.funnelStages, newStage]
      : [newStage];

    return {
      ...state,
      funnelStages: newFunnelStages,
    };
  },
  [EditMultichatFunnelStage.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {funnelStages} = state;
    const {funnelStage} = action;

    if (!funnelStages) {
      return state;
    }

    const newFunnelStages = funnelStages.map(stage =>
      stage.id === funnelStage.id ? funnelStage : stage
    );

    let currentFunnel = state.currentFunnel;
    if (state.currentFunnel) {
      const funnelLineIndex = currentFunnel.lanes.findIndex(
        lane => lane.stage.id === funnelStage.id
      );

      if (funnelLineIndex !== -1) {
        currentFunnel = setIn(
          currentFunnel,
          ['lanes', funnelLineIndex, 'stage'],
          funnelStage
        );
      }
    }

    return {
      ...state,
      funnelStages: newFunnelStages,
      currentFunnel,
    };
  },
  [AddConversationsToLane.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {laneId, conversations} = action;
    if (!laneId || !conversations) return state;
    const {currentFunnel} = state;
    if (!currentFunnel) return state;

    const newLanes = currentFunnel.lanes.map(laneItem => {
      if (laneItem.id === laneId) {
        return {
          ...laneItem,
          conversations: [...laneItem.conversations, ...conversations],
          conversationPagination: {
            ...laneItem.conversationPagination,
            page: laneItem.conversationPagination.page + 1,
          },
        };
      }
      return laneItem;
    });

    return {
      ...state,
      currentFunnel: {
        ...currentFunnel,
        lanes: newLanes,
      },
    };
  },
  [DragAndDropConversations.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    if (!action.dragAndDropResult || !state.currentFunnel) return state;
    const {source, destination} = action.dragAndDropResult;

    const lanes = List(state.currentFunnel?.lanes || []);
    if (!destination) {
      return;
    }

    if (source.laneId === destination.laneId) {
      const sourceLane = lanes.find(lane => lane.id === source.laneId);
      const sourceConversations = sourceLane?.conversations || [];
      const items = arrayMove(
        sourceConversations,
        source.index,
        destination.index
      );

      const newLanes = lanes.update(
        lanes.findIndex(lane => lane.id === source.laneId),
        lane => ({...lane, conversations: items})
      );

      return {
        ...state,
        currentFunnel: {
          ...state.currentFunnel,
          lanes: newLanes.toJS(),
        },
      };
    }
    const sourceLane = lanes.find(lane => lane.id === source.laneId);
    const destinationLane = lanes.find(lane => lane.id === destination.laneId);

    if (!sourceLane || !destinationLane) {
      return state;
    }

    const sourceConversations = sourceLane.conversations || [];
    const destinationConversations = destinationLane.conversations || [];

    const result = moveBetweenArrays(
      sourceConversations,
      destinationConversations,
      source.index,
      destination.index
    );

    const newSourceTotalConversations =
      sourceLane.conversationPagination.total - 1;
    const newDestinationTotalConversations =
      destinationLane.conversationPagination.total + 1;

    const sourceIndex = lanes.findIndex(lane => lane.id === source.laneId);
    const destinationIndex = lanes.findIndex(
      lane => lane.id === destination.laneId
    );

    const newLanes = lanes
      .update(sourceIndex, lane => ({
        ...lane,
        conversations: result.source,
        conversationPagination: {
          ...lane.conversationPagination,
          total: newSourceTotalConversations,
        },
      }))
      .update(destinationIndex, lane => ({
        ...lane,
        conversations: result.destination,
        conversationPagination: {
          ...lane.conversationPagination,
          total: newDestinationTotalConversations,
        },
      }));

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes.toJS(),
      },
    };
  },
  [SetFunnelMainLane.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {laneId} = action;
    if (!laneId || !state.currentFunnel) return state;

    const newLanes = state.currentFunnel.lanes.map(lane => {
      if (lane.id === laneId) {
        return {
          ...lane,
          setting: true,
        };
      }
      return {
        ...lane,
        setting: undefined,
      };
    });

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes,
      },
    };
  },
  [UpsertFunnelConversation.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {conversation} = action;
    if (!conversation || !state.currentFunnel) return state;
    if (conversation.crmConversationState === 'CLOSED') return state;

    const lanes = List(state.currentFunnel.lanes || []);

    // Find the lane and conversation index
    // to update or insert the new conversation
    let laneIndex = -1;
    let conversationIndexInLane = -1;

    for (let i = 0; i < lanes.size; i++) {
      const lane = lanes.get(i);
      conversationIndexInLane = lane.conversations.findIndex(
        item => item.id === conversation.id
      );
      if (conversationIndexInLane !== -1) {
        laneIndex = i;
        break;
      }
    }

    if (laneIndex === -1 || conversationIndexInLane === -1) {
      const mainLane = lanes.find(lane => !!lane.setting);
      if (mainLane) {
        mainLane.conversations = [conversation, ...mainLane.conversations];
        mainLane.conversationPagination.total += 1;
        return {
          ...state,
          currentFunnel: {
            ...state.currentFunnel,
            lanes: lanes.toJS(),
          },
        };
      }
      return state;
    }

    const newLanes = lanes.update(laneIndex, lane => ({
      ...lane,
      conversations: lane.conversations.map(item =>
        item.id === conversation.id ? conversation : item
      ),
    }));

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes.toJS(),
      },
    };
  },
  [UpdateFunnelConversation.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {conversation} = action;
    if (!conversation || !state.currentFunnel) return state;

    const lanes = List(state.currentFunnel.lanes || []);

    // Find the lane and conversation index
    // to update the conversation
    let laneIndex = -1;
    let conversationIndexInLane = -1;

    for (let i = 0; i < lanes.size; i++) {
      const lane = lanes.get(i);
      conversationIndexInLane = lane.conversations.findIndex(
        item => item.id === conversation.id
      );
      if (conversationIndexInLane !== -1) {
        laneIndex = i;
        break;
      }
    }

    if (laneIndex === -1 || conversationIndexInLane === -1) {
      return state;
    }

    const newLanes = lanes.update(laneIndex, lane => ({
      ...lane,
      conversations: lane.conversations.map(item =>
        item.id === conversation.id ? conversation : item
      ),
    }));

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes.toJS(),
      },
    };
  },
  [CloseFunnelConversation.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {conversationId} = action;
    if (!conversationId || !state.currentFunnel) return state;

    const lanes = List(state.currentFunnel.lanes || []);

    // Find the lane and conversation index
    // to remove the conversation

    let laneIndex = -1;
    let conversationIndexInLane = -1;

    for (let i = 0; i < lanes.size; i++) {
      const lane = lanes.get(i);
      conversationIndexInLane = lane.conversations.findIndex(
        item => item.id === conversationId
      );
      if (conversationIndexInLane !== -1) {
        laneIndex = i;
        break;
      }
    }

    if (laneIndex === -1 || conversationIndexInLane === -1) {
      return state;
    }

    const newLanes = lanes.update(laneIndex, lane => ({
      ...lane,
      conversations: lane.conversations.filter(
        item => item.id !== conversationId
      ),
      conversationPagination: {
        ...lane.conversationPagination,
        total: lane.conversationPagination.total - 1,
      },
    }));

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes.toJS(),
      },
    };
  },
  [RemoveConversationFromLane.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {laneId, conversationId} = action;
    if (!laneId || !conversationId || !state.currentFunnel) return state;

    const lanes = List(state.currentFunnel.lanes || []);

    const laneIndex = lanes.findIndex(l => l.id === laneId);

    if (laneIndex === -1) {
      return state;
    }

    const newLanes = lanes.update(laneIndex, lane => ({
      ...lane,
      conversations: lane.conversations.filter(
        item => item.id !== conversationId
      ),
      conversationPagination: {
        ...lane.conversationPagination,
        total: lane.conversationPagination.total - 1,
      },
    }));

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes.toJS(),
      },
    };
  },
  [DeleteLaneFromCurrentMultichatFunnel.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {laneId} = action;
    if (!laneId || !state.currentFunnel) return state;

    const newLanes = state.currentFunnel.lanes.filter(
      lane => lane.id !== laneId
    );

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes: newLanes,
      },
    };
  },
  [ReorderFunnelLanes.type]: (
    state: MultichatFunnelState,
    action: MultichatFunnelAction
  ): MultichatFunnelState => {
    const {lanes} = action;
    if (!lanes || !state.currentFunnel) return state;

    return {
      ...state,
      currentFunnel: {
        ...state.currentFunnel,
        lanes,
      },
    };
  },
});
